// @ts-nocheck
import React, { Suspense, lazy, useContext, useEffect, useRef, useState } from 'react';
import { Routes, Route, Outlet, Navigate, useLocation, useParams, matchPath } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { navClose } from 'store/legacyActionsBridge';

import './index.css';
import './chart.css';
import './step.css';

import Nav from './layouts/nav';
import { PageLocationArea } from './layouts/nav/PageLocation';
import ScrollToTop from './layouts/ScrollToTop';
import Footer from '../layout/Footer';
import { ThemeContext } from '../context/ThemeContext';
import { getRoleRoutes, routeRegistry } from './routes/routeRegistry';
import { resolveAppRole } from './routes/routeAccess';
import TourHost from '../shared/onboarding/TourHost';
import { getCurrentUserId } from '../shared/utils/ownedCompanies';
import { getActiveConnectEventQuietly } from '../shared/api/services/EventsService';
import { ACTIVE_EVENT_SELECTION_CHANGED, readSelectedActiveEventId } from '../features/events/active-events';
import {
  fetchEventVisibilitySnapshot,
  getActiveEventIdFromStorage,
  setCachedEventAdminPanelAccess,
} from '../features/events/event-access';

import Error400 from './pages/Error400';
import Error403 from './pages/Error403';
import Error404 from './pages/Error404';
import Error500 from './pages/Error500';
import Error503 from './pages/Error503';
/*
 * FAZ 7 / F7-6 — Bu ikisi lazy: `UserManualPage` 3.032 satir ve yalnizca
 * `/usermanual` altinda; `DigitalCardPublic` public bir goruntuleme sayfasi.
 * Ikisi de App.tsx'teki dis Suspense sinirinin ICINDE render ediliyor.
 */
const DigitalCardPublic = lazy(() => import('./pages/DigitalCardPublic'));
const PublicTableListPage = lazy(() => import('../features/tables/public-table-list-page'));
const PublicTablePage = lazy(() => import('../features/tables/public-table-page'));
const UserManualPage = lazy(() => import('../features/usermanual/pages/UserManualPage'));
const ZiraatPayResultPage = lazy(() => import('../features/tickets/ziraatpay-result'));
const VerifyEmail = lazy(() => import('../features/auth/pages/VerifyEmail'));
const LiveBoardPage = lazy(() => import('../features/b2b-live-board/live-board-page'));

const HOME_PATH = '/home';

const LegacyLiveBoardRedirect = () => {
  const { sessionId } = useParams();
  return <Navigate to={`/b2b-vall/${sessionId}`} replace />;
};

// Rota listesi kismen ASENKRON gelen yetki bilgisine bagli; hazir olmadigi
// kisa anda kullaniciya hata degil bu yukleniyor durumu gosteriliyor.

const RouteAccessLoader = () => (
  <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '100vh' }}>
    <div className='spinner-border text-primary' role='status'>
      <span className='visually-hidden'>Yükleniyor…</span>
    </div>
  </div>
);

// Adres, herhangi bir rol icin tanimli bir rotaya karsilik geliyor mu? Erisim
// bilgisi henuz gelmemisken bu adresler ana sayfaya atilmiyor, beklenip
// yetki netlesince karar veriliyor.
const isKnownRoutePath = (pathname) =>
  routeRegistry.some((route) => route.path && matchPath({ path: `/${route.path}`, end: true }, pathname));

const Markup = () => {
  const [, setAccessVersion] = useState(0);
  // Ortak etkinlik secimi degisince admin paneli erisimi yeniden hesaplanir.
  const [selectionVersion, setSelectionVersion] = useState(0);
  useEffect(() => {
    const bump = () => {
      setSelectionVersion((prev) => prev + 1);
      setAccessVersion((prev) => prev + 1);
    };
    window.addEventListener(ACTIVE_EVENT_SELECTION_CHANGED, bump);
    return () => window.removeEventListener(ACTIVE_EVENT_SELECTION_CHANGED, bump);
  }, []);
  // Asenkron admin paneli erisim kontrolu tamamlandi mi (basarili/basarisiz/zaman asimi).
  const [accessResolved, setAccessResolved] = useState(false);
  const appRole = resolveAppRole();
  const allroutes = getRoleRoutes(appRole);

  // `resolveAppRole()` ve `getRoleRoutes()` senkron calisiyor ama yetki bilgisinin
  // bir kismi yalnizca asagidaki effect'in getirdigi ASENKRON participation-status
  // cevabindan (`canAccessAdminPanel`) anlasilabiliyor; o cevap gelene kadar
  // `b2b-admin-*` rotalari listede olmayabilir. Bu yuzden "eslesmedi" asla
  // "sayfa yok" olarak yorumlanmiyor (bkz. NoMatch).

  useEffect(() => {
    const handleAccessUpdated = () => setAccessVersion((prev) => prev + 1);
    window.addEventListener('app-access-updated', handleAccessUpdated);
    return () => {
      window.removeEventListener('app-access-updated', handleAccessUpdated);
    };
  }, []);

  // `resolveAppRole()` yukarida SENKRON calisiyor ama b2b_admin yetkisi bazi
  // kullanicilar icin yalnizca ASENKRON participation-status cevabindan
  // (`canAccessAdminPanel`) anlasilabiliyor. Bu cevap normalde Nav/SideBar
  // (`useMenuFilter`) tarafindan `MainLayout` mount oldugunda getirilip
  // `setCachedEventAdminPanelAccess` ile cache'leniyor. SORUN: sayfa
  // yenilendiginde bu cache bos baslar; ilk render'da rol yanlislikla
  // 'user' cikar, `b2b-admin-*` rotalari listeden dusuyor ve `<Routes>`
  // '*' (NotFound) rotasina duser — ki bu rota `MainLayout`'un DISINDA,
  // yani Nav hic mount olmuyor ve cache asla dogru degerle guncellenmiyor.
  // Kilitlenmeyi kirmak icin ayni getiriyi burada, hangi rota eslesirse
  // eslessin calisacak sekilde tekrarliyoruz.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        let activeEventId = readSelectedActiveEventId() || getActiveEventIdFromStorage();
        if (!activeEventId) {
          const activeEvent = await getActiveConnectEventQuietly();
          activeEventId = String(
            activeEvent?.id ?? activeEvent?.eventId ?? activeEvent?.activeEventId ?? ''
          ).trim();
        }
        if (!active || !activeEventId) {
          return;
        }
        // Ag beklenmedik sekilde hiç donmezse burada takili kalinmasin diye
        // guvenlik zaman asimi.
        const timeout = new Promise((resolve) => setTimeout(() => resolve(null), 6000));
        const snapshot = await Promise.race([fetchEventVisibilitySnapshot(activeEventId), timeout]);
        if (!active) return;
        if (snapshot) {
          setCachedEventAdminPanelAccess({
            eventId: activeEventId,
            canAccessAdminPanel: snapshot.canAccessAdminPanel,
          });
        }
      } catch {
        // sessizce vazgec — Nav mount oldugunda kendi effect'i tekrar dener
      } finally {
        if (active) setAccessResolved(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [selectionVersion]);

  // Oturum acikken kullaniciya HICBIR kosulda 404 ekrani gosterilmiyor.
  // Rota listesi rol/yetki hesabina bagli ve bunun bir kismi asenkron geldigi
  // icin, "eslesmedi" demek "boyle bir sayfa yok" demek degil; cogu zaman
  // sadece "henuz hazir degil" demek. Bu yuzden eslesmeyen her adres ana
  // sayfaya yonlendiriliyor.
  function NoMatch() {
    const { pathname } = useLocation();

    // '/home' bile eslesmiyorsa yonlendirme sonsuz donguye girer; bunun yerine
    // rota listesi tamamlanana kadar bekliyoruz.
    if (pathname === HOME_PATH) {
      return <RouteAccessLoader />;
    }

    // Dogrudan URL ile acilan (ör. /supplier-day-applications) yetkiye bagli
    // rotalar ilk render'da listede olmayabiliyor; erisim kontrolu bitmeden
    // ana sayfaya yonlendirilirse adres kaybolur.
    if (!accessResolved && isKnownRoutePath(pathname)) {
      return <RouteAccessLoader />;
    }
    return <Navigate to={HOME_PATH} replace />;
  }

  return (
    <>
      <Routes>
        {/*
          Giris tamamlandiginda oturum durumu ile adres cubugunun guncellenmesi
          ayni an olmayabiliyor; URL bir an '/login' iken burasi render edilirse
          bu rota olmadan '*' (NotFound) esleser ve 404 flash'lardi. Oturum
          acikken /login'in zaten isi yok.
        */}
        <Route path='/login' element={<Navigate to='/home' replace />} />
        <Route path='/ziraatpay-result' element={<ZiraatPayResultPage />} />
        <Route
          path='/page-verify-email'
          element={
            <div style={{ position: 'relative', height: '100vh' }}>
              <VerifyEmail />
            </div>
          }
        />
        <Route path='/usermanual/*' element={<UserManualPage />} />
        <Route path='/digital-card/:cardId/:slug?' element={<DigitalCardPublic />} />
        <Route path='/b2b' element={<PublicTableListPage />} />
        <Route path='/b2b/:tableId' element={<PublicTablePage />} />
        {/* TV ekrani: tam ekran, MainLayout disinda; yetki backend'de kontrol ediliyor. */}
        <Route path='/b2b-vall' element={<LiveBoardPage />} />
        <Route path='/b2b-vall/:sessionId' element={<LiveBoardPage />} />
        {/* Eski /vall linkleri yeni adrese yönlenir. */}
        <Route path='/vall' element={<Navigate to='/b2b-vall' replace />} />
        <Route path='/vall/:sessionId' element={<LegacyLiveBoardRedirect />} />
        <Route path='/page-error-400' element={<Error400 />} />
        <Route path='/page-error-403' element={<Error403 />} />
        <Route path='/page-error-404' element={<Error404 />} />
        <Route path='/page-error-500' element={<Error500 />} />
        <Route path='/page-error-503' element={<Error503 />} />

        <Route element={<MainLayout />}>
          {allroutes.map((data, i) => (
            <Route key={`${data.path}-${i}`} path={data.path} element={data.component} />
          ))}
        </Route>

        <Route path='*' element={<NoMatch />} />
      </Routes>
      <ScrollToTop />
    </>
  );
};

function MainLayout() {
  const { sidebariconHover } = useContext(ThemeContext);
  const dispatch = useDispatch();
  const location = useLocation();
  const sideMenu = useSelector((state) => state.sideMenu);
  const routeKey = `${location.pathname}${location.search}${location.hash}`;
  const previousRouteKeyRef = useRef(routeKey);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isTabletOrMobile = window.matchMedia('(max-width: 1299.98px)').matches;
    if (!isTabletOrMobile) return;
    dispatch(navClose());
  }, [dispatch]);

  useEffect(() => {
    if (previousRouteKeyRef.current === routeKey) return;
    previousRouteKeyRef.current = routeKey;
    if (!sideMenu) return;
    if (typeof window === 'undefined') return;

    const isTabletOrMobile = window.matchMedia('(max-width: 1299.98px)').matches;
    if (!isTabletOrMobile) return;

    dispatch(navClose());
  }, [dispatch, routeKey, sideMenu]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const syncBodyScroll = () => {
      const isTabletOrMobile = window.matchMedia('(max-width: 1299.98px)').matches;
      document.body.style.overflow = sideMenu && isTabletOrMobile ? 'hidden' : '';
    };

    syncBodyScroll();
    window.addEventListener('resize', syncBodyScroll);

    return () => {
      window.removeEventListener('resize', syncBodyScroll);
      document.body.style.overflow = '';
    };
  }, [sideMenu]);

  return (
    <div
      id='main-wrapper'
      className={`show web-user-app ${sidebariconHover ? 'iconhover-toggle' : ''} ${sideMenu ? 'menu-toggle' : ''}`}
      style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
    >
      <Nav />
      <div className='content-body app-content-body' style={{ flex: '1 0 auto' }}>
        <div className='container-fluid app-container-fluid'>
          <PageLocationArea>
            {/*
              FAZ 7 / F7-6 — 🔴 ZORUNLU SUSPENSE SINIRI.

              `routeRegistry` artik 45 sayfayi `lazy()` ile yukluyor. `lazy()`
              bileşeni Suspense olmadan render edilirse React FIRLATIR.

              ⚠️ Neden App.tsx'teki dis sinir YETMEZ: o sinir tum kabugu
              (Nav + Footer) sariyor. Bir rota suspend ettiginde kabuk KOMPLE
              unmount olup tam sayfa preloader'i gorunurdu — her navigasyonda
              sayfa "sifirlanmis" gibi hissedilirdi. Bu ic sinir yalnizca
              icerik alanini askiya alir; Nav ve Footer yerinde kalir.
            */}
            <Suspense
              fallback={
                <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '40vh' }}>
                  <div className='spinner-border text-primary' role='status'>
                    <span className='visually-hidden'>Yükleniyor…</span>
                  </div>
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </PageLocationArea>
        </div>
      </div>
      <Footer />
      {/* Faz 4.2: sayfalari gezen karsilama turu + sayfa ilk acildiginda sayfa turu (yonetici haric). */}
      <TourHost userId={String(getCurrentUserId() || '')} pageToursEnabled={resolveAppRole() !== 'b2b_admin'} />
    </div>
  );
}

export default Markup;
